import { Component, ViewChild, inject } from '@angular/core';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { OptimizationConfigurationService, ProblemInstanceService, ConfigurableOfficeInfo } from 'planner-shared';

import { OptimizationPreferencesComponent } from './preferences/optimization-preferences.component';
import { OptimizationSettingsComponent } from './settings/optimization-settings.component';

@Component({
    selector: 'app-optimization-settings-modal',
    templateUrl: './optimization-settings-modal.component.html',
    styleUrls: ['./optimization-settings-modal.component.scss'],
    standalone: false
})
export class OptimizationSettingsModalComponent {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly optimizationConfigurationService = inject(OptimizationConfigurationService);

    public changed: boolean;
    public revertResetVisible: boolean;
    public loadingHeavyTaskCapacity: boolean;
    public saving: boolean;
    public confirm$: Subject<void>;
    public cancel$: Subject<void>;

    @ViewChild(OptimizationPreferencesComponent)
    private optimizationSettingsPreferencesComponent: OptimizationPreferencesComponent;
    @ViewChild(OptimizationSettingsComponent)
    private optimizationSettingsComponent: OptimizationSettingsComponent;

    constructor() {
        this.confirm$ = new Subject();
        this.cancel$ = new Subject();
    }

    public dismissModal(): void {
        this.cancel$.next();
    }

    public save(): void {
        if (!this.changed) {
            return;
        }

        const configurations = <ConfigurableOfficeInfo>{};
        this.optimizationSettingsPreferencesComponent.mapConfiguration(configurations);
        this.optimizationSettingsComponent.mapConfiguration(configurations);

        this.saving = true;
        this.optimizationConfigurationService
            .updateOptimizationConfigurations(configurations)
            .pipe(
                catchError(() => throwError('Optimization configuration failed.')),
                finalize(() => (this.saving = false))
            )
            .subscribe(() => {
                this.optimizationSettingsPreferencesComponent.updateProblemInstance();
                this.optimizationSettingsComponent.updateProblemInstance();
                this.confirm$.next();
            });
    }

    public settingsChanged(): void {
        this.changed = true;
        this.revertResetVisible = false;
    }

    public heavyTaskCapacityLoaded(loading: boolean): void {
        this.loadingHeavyTaskCapacity = loading;
    }

    public resetDefault(): void {
        this.changed = true;
        this.revertResetVisible = true;
        this.optimizationSettingsPreferencesComponent.reset();
        this.optimizationSettingsComponent.reset();
    }

    public revertReset(): void {
        this.changed = false;
        this.revertResetVisible = false;
        this.problemInstanceService.getProblemInstance().subscribe(result => {
            this.optimizationSettingsPreferencesComponent.reset(result.problemInstance.officeInfo);
            this.optimizationSettingsComponent.reset(result.problemInstance.officeInfo);
        });
    }
}
